import { AlertCircle, Phone } from "lucide-react";
import Seo from "../components/Seo";
import PageHero from "../components/PageHero";
import { Section, SectionHeading } from "../components/Section";
import Reveal from "../components/Reveal";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";
import { LOCATIONS, REQUIREMENTS } from "../lib/content";

const WB = LOCATIONS["west-branch"];

export default function RentalRequirements() {
  return (
    <>
      <Seo
        title="Rental Requirements, Cancellation & Weather Policy | West Branch Marina"
        description="Renters must be 18 with a valid driver's license. Ohio boater education rules, the $20 Flexibility Fee cancellation option, weather policy, pet policy and watersports towing rules."
        path="/rental-requirements"
        image="/images/watersports_tube.jpeg"
      />

      <PageHero
        image="/images/watersports_tube.jpeg"
        alt="Rider on an inflatable tube being towed behind a boat"
        eyebrow="Rental requirements"
        title="Read this before you book"
        subtitle="Age and licensing, Ohio boater education, cancellation, weather, pets and towing — all of it in plain language."
      />

      <Section tone="white">
        <Reveal
          data-testid="boater-exam-notice"
          className="flex gap-4 rounded-2xl border border-lake/30 bg-lake/[0.06] p-6"
        >
          <AlertCircle className="mt-0.5 h-6 w-6 shrink-0 text-lake" aria-hidden="true" />
          <div>
            <h2 className="font-display text-lg font-semibold text-navy">Ohio boater education</h2>
            <p className="mt-2 text-sm leading-relaxed text-slate-700">
              Ohio residents born on or after <strong className="font-bold text-navy">January 1, 1982</strong> must hold
              an Ohio boater's license, or pass a 10-question boating exam with at least{" "}
              <strong className="font-bold text-navy">90% accuracy</strong>.
            </p>
          </div>
        </Reveal>

        <div className="mt-12">
          <SectionHeading eyebrow="The details" title="Requirements & policies" />
          <Accordion
            type="single"
            collapsible
            className="mt-8 divide-y divide-sand-border overflow-hidden rounded-2xl border border-sand-border bg-sand"
          >
            {REQUIREMENTS.map((r) => (
              <AccordionItem key={r.id} value={r.id} className="border-0 px-5">
                <AccordionTrigger
                  data-testid={`requirement-trigger-${r.id}`}
                  className="py-5 text-left font-display text-base font-semibold text-navy hover:no-underline sm:text-lg"
                >
                  {r.title}
                </AccordionTrigger>
                <AccordionContent
                  data-testid={`requirement-content-${r.id}`}
                  className="pb-5 text-sm leading-relaxed text-slate-700"
                >
                  {r.body}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>

        <Reveal
          data-testid="requirements-flexibility-callout"
          className="mt-12 grid gap-6 rounded-2xl border border-sand-border bg-sand p-7 sm:grid-cols-2"
        >
          <div>
            <p className="eyebrow text-lake">With the $20 Flexibility Fee</p>
            <p className="mt-3 text-sm leading-relaxed text-slate-700">
              Cancel up to <strong className="font-bold text-navy">24 hours</strong> in advance for a full rental refund.
              The fee itself is non-refundable and must be purchased at the time of the initial booking.
            </p>
          </div>
          <div>
            <p className="eyebrow text-lake">Without it</p>
            <p className="mt-3 text-sm leading-relaxed text-slate-700">
              Cancellation requires <strong className="font-bold text-navy">72 hours</strong> notice and incurs a{" "}
              <strong className="font-bold text-navy">$75</strong> fee. No-shows are charged full price. Same-day and
              hourly reservations under 24 hours are non-cancellable.
            </p>
          </div>
        </Reveal>

        <div className="mt-10 flex flex-col items-start gap-4 sm:flex-row sm:items-center">
          <a
            href={WB.phoneHref}
            data-testid="requirements-call-link"
            className="inline-flex items-center gap-2 rounded-full bg-navy px-6 py-3 text-sm font-bold text-white transition-colors hover:bg-navy-surface"
          >
            <Phone className="h-4 w-4" aria-hidden="true" />
            Questions? Call {WB.phone}
          </a>
          <p className="text-sm text-slate-600">
            Transfers to another day in the current season can be arranged by phone.
          </p>
        </div>
      </Section>
    </>
  );
}
